import React, { useState, useEffect } from 'react';
import { Globe, Save, Lock, ShieldCheck, FileCheck, Loader2, Award, DollarSign } from 'lucide-react';
import { supabase } from "../lib/supabase"; 
import toast from "react-hot-toast";

export default function Settings() {
  const [activeTab, setActiveTab] = useState('general');  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Fixed ID for single-row global settings
  const SETTINGS_ID = 1;
  
  const [formData, setFormData] = useState({
    company_name: '',
    proprietor_director: '',
    official_email: '',
    contact_phone: '',
    registered_address: '',
    total_amount: '',
    total_turnover: '',
    total_cash: '',
    hpcl_vendor_code: '',
    gstin_number: '',
    pan_card_no: '',
    valid_permits_site_work: '',
    peso_license_no: '',
    peso_expiry_date: '',
    labour_license_no: '',
    workmen_insurance: ''
  });

  useEffect(() => { 
    fetchSettings(); 
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('settings').select('*').eq('id', SETTINGS_ID).maybeSingle();
      if (error) throw error;
      if (data) setFormData(data);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const payload = {
        id: SETTINGS_ID,
        ...formData,
        // Ensure numeric fields are correctly parsed before sending to Supabase
        total_amount: formData.total_amount ? parseFloat(formData.total_amount) : 0,
        total_turnover: formData.total_turnover ? parseFloat(formData.total_turnover) : 0,
        total_cash: formData.total_cash ? parseFloat(formData.total_cash) : 0,
        peso_expiry_date: formData.peso_expiry_date || null,
        updated_at: new Date()
      };

      const { error } = await supabase.from('settings').upsert(payload);
      if (error) throw error;
      toast.success("Corporate records updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Update failed. Check your network or permissions.");
    } finally { 
      setSaving(false); 
    }
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  if (loading) return <div className="flex justify-center p-20 text-[#0054A6]"><Loader2 className="animate-spin" size={40} /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-700 pb-20">
      <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
        <div className="flex flex-col md:flex-row h-full min-h-[600px]">
          
          {/* Navigation Sidebar */}
          <aside className="w-full md:w-72 bg-slate-50 p-6 space-y-2 border-r border-slate-200">
            <p className="px-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Enterprise Config</p>
            <TabButton active={activeTab === 'general'} onClick={() => setActiveTab('general')} icon={<Globe size={18}/>} label="Company Profile" />
            <TabButton active={activeTab === 'compliance'} onClick={() => setActiveTab('compliance')} icon={<ShieldCheck size={18}/>} label="Vendor Compliance" />
            <TabButton active={activeTab === 'finance'} onClick={() => setActiveTab('finance')} icon={<DollarSign size={18}/>} label="Financials" />
            <TabButton active={activeTab === 'security'} onClick={() => setActiveTab('security')} icon={<Lock size={18}/>} label="System Access" />
          </aside>

          {/* Main Form Content */}
          <main className="flex-1 p-6 md:p-12 bg-white">
            {activeTab === 'general' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Company Profile</h2>
                  <p className="text-sm text-slate-500 font-medium mt-1">Official identity details for HPCL billing and project records.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SettingInput label="Company Name" value={formData.company_name} onChange={(v) => handleChange('company_name', v)} />
                  <SettingInput label="Proprietor/Director" value={formData.proprietor_director} onChange={(v) => handleChange('proprietor_director', v)} />
                  <SettingInput label="Official Email" value={formData.official_email} onChange={(v) => handleChange('official_email', v)} />
                  <SettingInput label="Contact Phone" value={formData.contact_phone} onChange={(v) => handleChange('contact_phone', v)} />
                  <div className="md:col-span-2">
                    <SettingInput label="Registered Office Address" value={formData.registered_address} onChange={(v) => handleChange('registered_address', v)} isArea />
                  </div>
                </div>
                <SaveButton onSave={handleSave} loading={saving} />
              </div>
            )}

            {activeTab === 'compliance' && (
              <div className="space-y-12">
                <section className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 text-[#0054A6] rounded-lg"><FileCheck size={20}/></div>
                    <div>
                      <h3 className="font-black text-slate-900">Statutory Details</h3>
                      <p className="text-[10px] text-[#EE3124] font-black tracking-wider uppercase">Taxation & Vendor Codes</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SettingInput label="HPCL Vendor Code" value={formData.hpcl_vendor_code} onChange={(v) => handleChange('hpcl_vendor_code', v)} />
                    <SettingInput label="GSTIN Number" value={formData.gstin_number} onChange={(v) => handleChange('gstin_number', v)} />
                    <SettingInput label="PAN Card No." value={formData.pan_card_no} onChange={(v) => handleChange('pan_card_no', v)} />
                  </div>
                </section>

                <hr className="border-slate-100" />

                <section className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 text-[#EE3124] rounded-lg"><Award size={20}/></div>
                    <div>
                      <h3 className="font-black text-slate-900">Safety & Licenses</h3>
                      <p className="text-[10px] text-[#0054A6] font-black tracking-wider uppercase">Valid Permits for Site Work</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SettingInput label="Valid Permits for Site Work" value={formData.valid_permits_site_work} onChange={(v) => handleChange('valid_permits_site_work', v)} />
                    <SettingInput label="PESO License No." value={formData.peso_license_no} onChange={(v) => handleChange('peso_license_no', v)} />
                    <SettingInput label="PESO Expiry Date" type="date" value={formData.peso_expiry_date} onChange={(v) => handleChange('peso_expiry_date', v)} />
                    <SettingInput label="Labour License No." value={formData.labour_license_no} onChange={(v) => handleChange('labour_license_no', v)} />
                    <SettingInput label="Workmen Insurance" value={formData.workmen_insurance} onChange={(v) => handleChange('workmen_insurance', v)} />
                  </div>
                </section>
                <SaveButton onSave={handleSave} loading={saving} />
              </div>
            )}

            {activeTab === 'finance' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Financial Summary</h2>
                  <p className="text-sm text-slate-500 font-medium mt-1">Track financial data, balances, and overall turnover.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SettingInput label="Total Amount" type="number" value={formData.total_amount} onChange={(v) => handleChange('total_amount', v)} />
                  <SettingInput label="Total Turnover" type="number" value={formData.total_turnover} onChange={(v) => handleChange('total_turnover', v)} />
                  <SettingInput label="Total Cash" type="number" value={formData.total_cash} onChange={(v) => handleChange('total_cash', v)} />
                </div>
                <SaveButton onSave={handleSave} loading={saving} />
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6 py-10 text-center">
                <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-4">
                  <Lock size={32} />
                </div>
                <h3 className="text-lg font-black text-slate-800">Security Credentials</h3>
                <p className="text-sm text-slate-500 max-w-xs mx-auto">Access levels and authentication logs are managed through the master administrative console.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function SaveButton({ onSave, loading }) {
  return (
    <div className="pt-6 border-t border-slate-100 flex justify-end">
      <button 
        disabled={loading} 
        onClick={onSave} 
        className="flex items-center gap-2 px-8 py-3.5 bg-[#0054A6] text-white rounded-xl font-black shadow-lg hover:bg-blue-800 transition-all active:scale-95 disabled:opacity-50"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
        {loading ? "Saving..." : "Update Records"}
      </button>
    </div>
  );
}

function TabButton({ active, icon, label, onClick }) {
  return (
    <button 
      onClick={onClick} 
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all ${active ? "bg-white text-[#0054A6] shadow-md" : "text-slate-500 hover:bg-white hover:text-slate-800"}`}
    >
      {icon} {label}
    </button>
  );
}

function SettingInput({ label, value, onChange, isArea = false, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] ml-1">{label}</label>
      {isArea ? (
        <textarea 
          value={value || ''} 
          onChange={(e) => onChange(e.target.value)} 
          className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent rounded-xl focus:bg-white focus:border-[#0054A6] outline-none font-bold text-sm transition-all min-h-[100px]" 
        />
      ) : (
        <input 
          type={type} 
          value={value || ''} 
          onChange={(e) => onChange(e.target.value)} 
          className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent rounded-xl focus:bg-white focus:border-[#0054A6] outline-none font-bold text-sm transition-all" 
        />
      )}
    </div>
  );
}